import axios from 'axios';

// For API calls via axios: use relative URL (works in all environments via ingress/CDN routing)
const API_BASE = '/api';

// For direct URLs (downloads, file links opened in new tabs): use the current origin at runtime
const getAbsoluteBase = () => window.location.origin;

const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('prixdeal_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Track whether a refresh is in progress to avoid concurrent refreshes
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// Handle auth errors with automatic token refresh
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // If 401 and we haven't retried yet and it's not the refresh endpoint itself
        if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh')) {
            const refreshToken = localStorage.getItem('prixdeal_refresh_token');

            if (!refreshToken) {
                // No refresh token, force logout
                localStorage.removeItem('prixdeal_token');
                localStorage.removeItem('prixdeal_user');
                localStorage.removeItem('prixdeal_refresh_token');
                window.location.href = '/#/connexion';
                return Promise.reject(error);
            }

            if (isRefreshing) {
                // Queue this request until the refresh completes
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers['Authorization'] = 'Bearer ' + token;
                    return api(originalRequest);
                }).catch(err => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const response = await axios.post(`${API_BASE}/auth/refresh`, {
                    refresh_token: refreshToken
                });

                const { token, refresh_token: newRefresh } = response.data;
                localStorage.setItem('prixdeal_token', token);
                localStorage.setItem('prixdeal_refresh_token', newRefresh);
                if (response.data.user) {
                    localStorage.setItem('prixdeal_user', JSON.stringify(response.data.user));
                }

                api.defaults.headers.common['Authorization'] = 'Bearer ' + token;
                processQueue(null, token);

                originalRequest.headers['Authorization'] = 'Bearer ' + token;
                return api(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                localStorage.removeItem('prixdeal_token');
                localStorage.removeItem('prixdeal_user');
                localStorage.removeItem('prixdeal_refresh_token');
                window.location.href = '/#/connexion';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

// Auth
export const authAPI = {
    register: (data) => api.post('/auth/register', data),
    login: (data) => api.post('/auth/login', data),
    loginSupplier: (data) => api.post('/auth/supplier/login', data),
    getMe: () => api.get('/auth/me'),
    getSupplierMe: () => api.get('/auth/supplier/me'),
    changePassword: (data) => api.put('/auth/change-password', data),
    updateProfile: (data) => api.put('/auth/profile', data),
};

// Supplier API
export const supplierAPI = {
    getProfile: () => api.get('/supplier/profile'),
    updateProfile: (data) => api.put('/supplier/profile', data),
    getProducts: () => api.get('/supplier/products'),
    updateProduct: (id, data) => api.put(`/supplier/products/${id}`, data),
    validateUpdates: () => api.post('/supplier/products/validate-updates'),
    getAnalytics: () => api.get('/supplier/analytics'),
    getOrders: () => api.get('/supplier/orders'),
    validateOrder: (id, data) => api.post(`/supplier/orders/${id}/validate`, data),
    generateDeliveryNote: (id, data, preview = false) => api.post(`/supplier/orders/${id}/delivery-note${preview ? '?preview=true' : ''}`, data, { responseType: 'blob' }),
    getMessages: () => api.get('/supplier/messages'),
    sendMessage: (data) => api.post('/supplier/messages', data),
    getUnreadCount: () => api.get('/supplier/messages/unread-count'),
    getDeliveryNotes: () => api.get('/supplier/delivery-notes'),
    downloadBatchBL: (ids) => api.post('/supplier/delivery-notes/download-batch', { ids }, { responseType: 'blob' }),
};

// Admin: Supplier Updates Validation
export const supplierUpdatesAPI = {
    getPending: () => api.get('/admin/supplier-updates'),
    approve: (productIds) => api.post('/admin/supplier-updates/approve', { product_ids: productIds }),
    reject: (productIds) => api.post('/admin/supplier-updates/reject', { product_ids: productIds }),
};

// Categories
export const categoriesAPI = {
    getAll: () => api.get('/categories'),
    create: (data) => api.post('/categories', data),
};

// Products
export const productsAPI = {
    getAll: (params) => api.get('/products', { params }),
    getAllAdmin: (params) => api.get('/admin/products', { params }),
    getById: (id) => api.get(`/products/${id}`),
    create: (data) => api.post('/admin/products', data),
    update: (id, data) => api.put(`/admin/products/${id}`, data),
    delete: (id) => api.delete(`/admin/products/${id}`),
    uploadImage: (id, formData) => api.post(`/admin/products/${id}/upload-image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    toggleActive: (id) => api.put(`/products/${id}/toggle-active`),
    toggleWeeklyOffer: (id) => api.put(`/products/${id}/toggle-weekly-offer`),
    getWeeklyOffers: () => api.get('/products/weekly-offers'),
    getWeeklyOffersByCategory: () => api.get('/products/weekly-offers-by-category'),
    getSeasonal: (month) => api.get('/products', { params: { season: month } }),
    seedProducts: () => api.post('/seed-products'),
    migrateCategories: () => api.post('/admin/migrate-categories'),
    bulkAction: (ids, action) => api.post('/admin/products/bulk-action', { ids, action }),
};

// Orders
export const ordersAPI = {
    create: (data) => api.post('/orders', data),
    getAll: (params) => api.get('/orders', { params }),
    getById: (id) => api.get(`/orders/${id}`),
    updateStatus: (id, status) => api.put(`/orders/${id}/status`, { status }),
    downloadPdf: (id) => api.get(`/orders/${id}/pdf`, { responseType: 'blob' }),
    sendToSuppliers: (id) => api.post(`/admin/orders/${id}/send-to-suppliers`),
    getSupplierOrders: (params) => api.get('/admin/supplier-orders', { params }),
    requestQuote: (data) => api.post('/quotes/request', data),
    bulkDelete: (payload) => api.post('/admin/orders/bulk-delete', payload),
};

// Stats
export const statsAPI = {
    getClientStats: () => api.get('/stats/client'),
    getAdminStats: () => api.get('/stats/admin'),
};

// Admin
export const adminAPI = {
    getClients: (params) => api.get('/admin/clients', { params }),
    createClient: (data) => api.post('/admin/clients', data),
    toggleClient: (id) => api.put(`/admin/clients/${id}/toggle`),
    updateClient: (id, data) => api.put(`/admin/clients/${id}`, data),
    deleteClient: (id) => api.delete(`/admin/clients/${id}`),
    createOrder: (data) => api.post('/admin/orders', data),
    getNotifications: () => api.get('/admin/notifications'),
    getNotificationCount: () => api.get('/admin/notifications/count'),
    markNotificationRead: (id) => api.put(`/admin/notifications/${id}/read`),
    markAllNotificationsRead: () => api.put('/admin/notifications/read-all'),
    backupDatabase: () => api.get('/admin/backup', { responseType: 'blob' }),
    restoreDatabase: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/admin/restore', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    sendWelcomeEmails: (clientIds) => api.post('/admin/clients/send-welcome', { client_ids: clientIds }),
    getEmailStats: () => api.get('/admin/email-stats'),
    getEmailLogs: (params) => api.get('/admin/email-logs', { params }),
    clearEmailHistory: () => api.delete('/admin/email-logs'),
    // Admin management
    getAdmins: () => api.get('/admin/admins'),
    createAdmin: (data) => api.post('/admin/admins', data),
    toggleAdmin: (id) => api.put(`/admin/admins/${id}/toggle`),
    // Suppliers
    getSuppliers: (params) => api.get('/admin/suppliers', { params }),
    getSupplier: (id) => api.get(`/admin/suppliers/${id}`),
    createSupplier: (data) => api.post('/admin/suppliers', data),
    updateSupplier: (id, data) => api.put(`/admin/suppliers/${id}`, data),
    deleteSupplier: (id) => api.delete(`/admin/suppliers/${id}`),
    getProductSuppliers: (productId) => api.get(`/admin/products/${productId}/suppliers`),
    updateProductSuppliers: (productId, data) => api.put(`/admin/products/${productId}/suppliers`, data),
    getSupplierStats: () => api.get('/admin/suppliers/stats/overview'),
    deleteAdmin: (id) => api.delete(`/admin/admins/${id}`),
    updateAdminProfile: (id, data) => api.put(`/admin/admins/${id}/profile`, data),
    getActivityLog: (params) => api.get('/admin/activity-log', { params }),
    getPermissions: () => api.get('/admin/permissions'),
    // Contacts
    getContacts: (params) => api.get('/admin/contacts', { params }),
    replyContact: (id, data) => api.post(`/admin/contacts/${id}/reply`, data),
    // Messaging
    getConversations: () => api.get('/admin/messages/conversations'),
    getMessages: (supplierId) => api.get(`/admin/messages/${supplierId}`),
    sendMessage: (supplierId, data) => api.post(`/admin/messages/${supplierId}`, data),
    // Delivery Notes (BL)
    getDeliveryNotes: () => api.get('/admin/delivery-notes'),
    downloadSingleBL: (id, preview = false) => api.post(`/admin/delivery-notes/${id}/download${preview ? '?preview=true' : ''}`, {}, { responseType: 'blob' }),
    downloadBatchBL: (ids) => api.post('/admin/delivery-notes/download-batch', { ids }, { responseType: 'blob' }),
    bulkDeleteDeliveryNotes: (payload) => api.post('/admin/delivery-notes/bulk-delete', payload),
};

// Client Categories (for pricing)
export const clientCategoriesAPI = {
    getAll: () => api.get('/client-categories'),
    create: (data) => api.post('/client-categories', data),
    update: (id, data) => api.put(`/client-categories/${id}`, data),
    delete: (id) => api.delete(`/client-categories/${id}`),
};

// CSV Export/Import - use runtime origin for direct download URLs

// Robust file download function using fetch + blob (works on all browsers including Edge)
export const downloadFile = async (url, filename) => {
    try {
        const response = await fetch(url);
        
        if (!response.ok) {
            throw new Error(`Erreur HTTP: ${response.status}`);
        }
        
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename || 'download';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        }, 100);
        
        return { success: true };
    } catch (error) {
        console.error('Download error:', error);
        return { success: false, error: error.message };
    }
};

export const csvAPI = {
    downloadProducts: async (token) => {
        const url = `${getAbsoluteBase()}/api/download/products?token=${token}`;
        return await downloadFile(url, 'produits_prixdeal.csv');
    },
    downloadClients: async (token) => {
        const url = `${getAbsoluteBase()}/api/download/clients?token=${token}`;
        return await downloadFile(url, 'clients_prixdeal.csv');
    },
    downloadOrders: async (token) => {
        const url = `${getAbsoluteBase()}/api/download/orders?token=${token}`;
        return await downloadFile(url, 'commandes_prixdeal.csv');
    },
    importProducts: (formData) => api.post('/import/products', formData, { headers: { 'Content-Type': 'multipart/form-data' }}),
    importClients: (formData) => api.post('/import/clients', formData, { headers: { 'Content-Type': 'multipart/form-data' }}),
    importClientsExcel: (formData) => api.post('/import/clients-excel', formData, { headers: { 'Content-Type': 'multipart/form-data' }}),
    importSupplierProducts: (formData) => api.post('/admin/import/supplier-products', formData, { headers: { 'Content-Type': 'multipart/form-data' }}),
    downloadSupplierTemplate: () => api.get('/admin/supplier-template', { responseType: 'blob' }),
    getSupplierImportHistory: () => api.get('/admin/supplier-imports'),
    deleteSupplierImport: (id) => api.delete(`/admin/supplier-imports/${id}`),
    clearSupplierImportHistory: () => api.delete('/admin/supplier-imports'),
};

export const priceImportAPI = {
    preview: (formData) => api.post('/admin/price-import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
    }),
    apply: (decisions) => api.post('/admin/price-import/apply', decisions),
};

// Catalogs API
export const catalogsAPI = {
    // Admin endpoints
    list: () => api.get('/admin/catalogs'),
    upload: (formData) => api.post('/admin/catalogs', formData, { headers: { 'Content-Type': 'multipart/form-data' }}),
    update: (id, data) => api.put(`/admin/catalogs/${id}`, data),
    delete: (id) => api.delete(`/admin/catalogs/${id}`),
    
    // Client endpoints
    listPublic: () => api.get('/catalogs'),
    getDownloadUrl: (catalogId) => `${getAbsoluteBase()}/api/catalogs/${catalogId}/download`,
};

// Custom Offers API
export const offersAPI = {
    // Admin endpoints
    list: () => api.get('/admin/offers'),
    get: (id) => api.get(`/admin/offers/${id}`),
    create: (data) => api.post('/admin/offers', data),
    update: (id, data) => api.put(`/admin/offers/${id}`, data),
    delete: (id) => api.delete(`/admin/offers/${id}`),
    bulkDelete: (payload) => api.post('/admin/offers/bulk-delete', payload),

    // Client endpoints
    getMyOffers: () => api.get('/my-offers'),
    getMyNotifications: () => api.get('/my-notifications'),
    markNotificationRead: (id) => api.put(`/notifications/${id}/read`),
};

// Best Practices API
export const bestPracticesAPI = {
    // Admin
    list: () => api.get('/admin/best-practices'),
    create: (formData) => api.post('/admin/best-practices', formData, { headers: { 'Content-Type': 'multipart/form-data' }}),
    update: (id, data) => api.put(`/admin/best-practices/${id}`, data),
    delete: (id) => api.delete(`/admin/best-practices/${id}`),
    getCategories: () => api.get('/best-practice-categories'),
    // Client
    getMine: () => api.get('/my-best-practices'),
    getFileUrl: (filename) => `${getAbsoluteBase()}/api/best-practices/files/${filename}`,
};

// Seed
export const seedAPI = {
    seed: () => api.post('/seed'),
};

// Settings
export const settingsAPI = {
    getHomepage: () => api.get('/settings/homepage'),
    getAll: () => api.get('/admin/settings'),
    update: (key, value) => api.put(`/admin/settings/${key}`, { value }),
    trackVisit: () => api.post('/track/visit'),
    getVisitorStats: () => api.get('/admin/visitor-stats'),
    getVisitors: (params) => api.get('/admin/visitors', { params }),
    backfillVisitorGeo: () => api.post('/admin/visitors/backfill-geo'),
    clearVisitorHistory: () => api.delete('/admin/visitors'),
};

// Quotes (admin)
export const quotesAPI = {
    list: (params) => api.get('/admin/quotes', { params }),
    getStats: () => api.get('/admin/quotes/stats'),
    getDetail: (id) => api.get(`/admin/quotes/${id}`),
    respond: (id, data) => api.put(`/admin/quotes/${id}/respond`, data),
    delete: (id) => api.delete(`/admin/quotes/${id}`),
    getMyQuotes: () => api.get('/my-quotes'),
    getByNumber: (num) => api.get(`/admin/quotes/by-number/${encodeURIComponent(num)}`),
};

export default api;
